// Iframe Integration Message Types
// VAN-021: Comprehensive postMessage API for iframe communication

import type { PaletteOptions } from "@mui/material/styles";

// Типы источников согласно архитектуре приложения
export type IframeSource = "preorder" | "qr" | "delivery";

// Типы тем для синхронизации
export type ThemeMode = "light" | "dark" | "auto" | "custom";

// Базовый интерфейс для всех сообщений iframe
export interface IframeMessage {
  type: MessageType;
  payload?: any;
  timestamp: number;
  source: IframeSource;
}

// Типы сообщений iframe
export type MessageType =
  | "PREORDER_APP_READY"
  | "PREORDER_COMPLETED"
  | "PREORDER_UPDATED"
  | "PREORDER_CANCELLED"
  | "PREORDER_ERROR"
  | "THEME_CHANGED"
  | "REQUEST_CLOSE"
  | "EDIT_ORDER";

// Данные готовности приложения
export interface AppReadyData {
  source: IframeSource;
  route: string;
  brandId?: string;
  pointId?: string;
  tableId?: string; // Только для QR режима
  capabilities: string[]; // Поддерживаемые функции: ["ordering", "theming", "closing"]
}

// Данные заказа согласно схеме OrderItem
export interface OrderData {
  orderId: string;
  items: Array<{
    uuid: string; // Используем uuid как в OrderItem
    name: string;
    quantity: number;
    price: number; // Цена в копейках для точности
    modifiers?: Array<{
      id: string;
      name: string;
      price: number; // Цена в копейках
      type: "addon" | "upgrade" | "customization";
    }>;
    comment?: string;
  }>;
  priceTotal: number; // Общая сумма в копейках (изменено с sum на priceTotal)
  comment?: string;
  brandId: string;
  pointId: string;
  timestamp: number;
  type: "PREORDER" | "DELIVERY" | "PICKUP";
}

// Упрощенная версия цветов для обратной совместимости
export interface SimpleThemeColors {
  primary?: string;
  secondary?: string;
  background?: string;
  surface?: string;
  accent?: string;
  text?: string;
  textSecondary?: string;
  border?: string;
  success?: string;
  warning?: string;
  error?: string;
  info?: string;
}

// Данные изменения темы
export interface ThemeChangeData {
  theme: ThemeMode;
  // colors может быть либо упрощенной версией, либо полной PaletteOptions из MUI
  // Полная версия используется SDK для передачи кастомных тем с mode и полной структурой
  colors?: SimpleThemeColors | PaletteOptions;
  // Полная кастомная цветовая схема в JSON
  customTheme?: {
    palette: Record<string, any>;
    typography?: Record<string, any>;
    spacing?: Record<string, any>;
    shadows?: Record<string, any>;
  };
}

// Данные запроса закрытия
export interface CloseRequestData {
  reason: "user_action" | "error" | "completion" | "cancel";
  message?: string;
}

// Данные ошибки
export interface ErrorData {
  error: string;
  code?: string;
  details?: any;
}

// Типизированные сообщения для каждого типа
export interface AppReadyMessage extends IframeMessage {
  type: "PREORDER_APP_READY";
  payload: AppReadyData;
}

export interface OrderCompletedMessage extends IframeMessage {
  type: "PREORDER_COMPLETED";
  payload: OrderData;
}

export interface OrderUpdatedMessage extends IframeMessage {
  type: "PREORDER_UPDATED";
  payload: OrderData;
}

export interface OrderCancelledMessage extends IframeMessage {
  type: "PREORDER_CANCELLED";
  payload?: undefined;
}

export interface OrderErrorMessage extends IframeMessage {
  type: "PREORDER_ERROR";
  payload: ErrorData;
}

export interface ThemeChangedMessage extends IframeMessage {
  type: "THEME_CHANGED";
  payload: ThemeChangeData;
}

export interface CloseRequestMessage extends IframeMessage {
  type: "REQUEST_CLOSE";
  payload: CloseRequestData;
}

// Сообщения от родителя к iframe
export interface ParentToIframeMessage {
  type: "SET_THEME" | "PARENT_READY" | "EDIT_ORDER" | "AUTH_CODE_TRANSFER";
  payload?: any;
  timestamp: number;
}

// Данные редактирования заказа от родителя
export interface EditOrderData {
  orderId: string;
}

// Данные передачи кода авторизации от родителя
export interface AuthCodeData {
  code: string;
  timestamp: number;
}

// Типизированное сообщение для редактирования заказа
export interface EditOrderMessage
  extends Omit<ParentToIframeMessage, "type" | "payload"> {
  type: "EDIT_ORDER";
  payload: EditOrderData;
}

// Типизированное сообщение для передачи кода авторизации
export interface AuthCodeTransferMessage
  extends Omit<ParentToIframeMessage, "type" | "payload"> {
  type: "AUTH_CODE_TRANSFER";
  payload: AuthCodeData;
}

// Union type для всех возможных сообщений
export type TypedIframeMessage =
  | AppReadyMessage
  | OrderCompletedMessage
  | OrderUpdatedMessage
  | OrderCancelledMessage
  | OrderErrorMessage
  | ThemeChangedMessage
  | CloseRequestMessage
  | EditOrderMessage;

// Функция валидации сообщений
export const isValidIframeMessage = (data: any): data is IframeMessage => {
  return (
    data &&
    typeof data === "object" &&
    typeof data.type === "string" &&
    [
      "PREORDER_APP_READY",
      "PREORDER_COMPLETED",
      "PREORDER_UPDATED",
      "PREORDER_CANCELLED",
      "PREORDER_ERROR",
      "THEME_CHANGED",
      "REQUEST_CLOSE",
      "EDIT_ORDER",
    ].includes(data.type) &&
    typeof data.timestamp === "number" &&
    ["preorder", "qr", "delivery"].includes(data.source)
  );
};

// Функция валидации сообщений от родителя
export const isValidParentMessage = (
  data: any
): data is ParentToIframeMessage => {
  return (
    data &&
    typeof data === "object" &&
    typeof data.type === "string" &&
    ["SET_THEME", "PARENT_READY", "EDIT_ORDER", "AUTH_CODE_TRANSFER"].includes(
      data.type
    ) &&
    typeof data.timestamp === "number"
  );
};

// Утилита для создания сообщений с timestamp
export const createIframeMessage = <T extends MessageType>(
  type: T,
  source: IframeSource,
  payload?: any
): IframeMessage => ({
  type,
  payload,
  timestamp: Date.now(),
  source,
});

// Утилита для создания сообщений от родителя
export const createParentMessage = (
  type: "SET_THEME" | "PARENT_READY" | "EDIT_ORDER" | "AUTH_CODE_TRANSFER",
  payload?: any
): ParentToIframeMessage => ({
  type,
  payload,
  timestamp: Date.now(),
});
